import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';

import { db } from '../../firebase.js';

// A record scope is one collection to read from, optionally bounded by the span during
// which that collection was the live source. App owners have one scope per collection
// spelling; machines have one per unit range.
export const buildAppRecordScopes = (collectionPaths, { companyId, unitId }) => (
    collectionPaths.map((collectionPath) => ({
        collectionPath,
        fallbackCompanyId: companyId ?? null,
        fallbackUnitId: unitId,
    }))
);

export const buildMachineRecordScopes = (unitRanges, { companyId }) => {
    const now = Date.now();

    return unitRanges.map((unitRange) => {
        const startDate = Number.isFinite(unitRange.start_date) ? unitRange.start_date : 0;
        const endDate = unitRange.current === true || !Number.isFinite(unitRange.end_date)
            ? now
            : unitRange.end_date;

        return {
            collectionPath: `units/${unitRange.unit_id}/dangerous-waste-records`,
            startDate,
            endDate,
            fallbackCompanyId: companyId ?? null,
            fallbackUnitId: unitRange.unit_id,
        };
    });
};

// Reads every scope over the given window and returns the merged records, newest first.
// A null window means the scope's own bounds are the only limit. `deduplicateById` is for
// scopes that hold the same records under different collection spellings; scopes that hold
// genuinely different records must leave it off, since ids are only unique per collection.
export const fetchDangerousWasteRecords = async (scopes, { startUnix, endUnix, deduplicateById = false } = {}) => {
    const hasWindow = Number.isFinite(startUnix) && Number.isFinite(endUnix);

    const scopeResults = await Promise.all(scopes.map(async (scope) => {
        const scopeStart = Number.isFinite(scope.startDate) ? scope.startDate : null;
        const scopeEnd = Number.isFinite(scope.endDate) ? scope.endDate : null;

        const queryStart = hasWindow
            ? Math.max(scopeStart ?? startUnix, startUnix)
            : scopeStart;
        const queryEnd = hasWindow
            ? Math.min(scopeEnd ?? endUnix, endUnix)
            : scopeEnd;

        if (Number.isFinite(queryStart) && Number.isFinite(queryEnd) && queryStart > queryEnd) {
            return [];
        }

        const constraints = [];
        if (Number.isFinite(queryStart)) constraints.push(where('timestamp', '>=', queryStart));
        if (Number.isFinite(queryEnd)) constraints.push(where('timestamp', '<=', queryEnd));
        constraints.push(orderBy('timestamp', 'desc'));

        const snapshot = await getDocs(query(collection(db, scope.collectionPath), ...constraints));

        return snapshot.docs.map((_doc) => {
            const recordData = _doc.data();
            return {
                ...recordData,
                company: recordData.company ?? scope.fallbackCompanyId,
                unit_id: recordData.unit_id ?? scope.fallbackUnitId,
                record_id: _doc.id,
                record_path: _doc.ref.path,
            };
        });
    }));

    let records = scopeResults.flat();

    if (deduplicateById) {
        const recordsById = new Map();
        records.forEach((record) => {
            if (!recordsById.has(record.record_id)) {
                recordsById.set(record.record_id, record);
            }
        });
        records = [...recordsById.values()];
    }

    return records
        .filter((record) => record.is_test !== true)
        .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
};
